import MapNavButton from '@locus-labs/mol-desktop-map-nav-button'
import Tooltip from '@locus-labs/mol-desktop-tooltip'
import FloatingActionButton from '@locus-labs/mol-mobile-floating-action-button'
import React, { useState, useRef } from 'react'
import { withTheme } from 'styled-components'

import ToolTipComponent from '../../../../src/ui/tooltips/ToolTipComponent.jsx'
import { widgetEnum } from '../../../../utils/constants.js'

const MAP_SHORTENER_URL = 'https://map-url.locuslabs.com/get-shortened-map-url'
const DEFAULT_INSTALL_ID = 'NO_INSTALL_ID'
const MAP_LINK_SHARE_BUTTON_DESKTOP = 'mapLinkShareButtonDesktop'
const MAP_LINK_SHARE_BUTTON_MOBILE = 'mapLinkShareButtonMobile'

const isSafari = navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') === -1

const isInIframe = () => {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

const getShortenedMapUrl = async (installId) => {
  const pageUrl = `${window.location.href}&refInstallId=${installId}`

  const res = await fetch(MAP_SHORTENER_URL, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ url: pageUrl })
  })
  if (!res.ok) {
    return false
  }
  const { shortUrl } = await res.json()
  return shortUrl
}

const copyToClipboard = (text) => {
  return navigator.clipboard.writeText(text).then(() => {
    return true
  }, (e) => {
    console.error('Copy to clipboard failed. Reason: ', e.message)
    return false
  })
}

const SHARING_BTN_STATE = {
  DEFAULT: 0,
  IN_PROGRESS: 1,
  READY_TO_COPY: 2,
  SUCCESS: 3,
  ERROR: 4
}

const LinkSharingButton = withTheme(({ T, theme, installId, app }) => {
  const [state, setState] = useState(SHARING_BTN_STATE.DEFAULT)
  const [shortUrl, setShortUrl] = useState(null)

  const focusAfterToastDismissRef = useRef(null)

  const onButtonClick = async () => {
    setState(SHARING_BTN_STATE.IN_PROGRESS)
    const shortenedUrl = await getShortenedMapUrl(installId)
    if (shortenedUrl) {
      if (isSafari || isInIframe()) {
        setShortUrl(shortenedUrl)
        setState(SHARING_BTN_STATE.READY_TO_COPY)
      } else {
        const didCopySucceed = await copyToClipboard(shortenedUrl)
        if (didCopySucceed) {
          setState(SHARING_BTN_STATE.SUCCESS)
          app.bus.send('toast/show', {
            message: app.gt()('mapLinkShareButton:Link successfully copied to clipboard'),
            iconName: 'success',
            background: theme.colors.statusSuccessBackground,
            focusAfterToastDismissRef
          })
        } else {
          setState(SHARING_BTN_STATE.ERROR)
          app.bus.send('toast/show', {
            message: app.gt()('mapLinkShareButton:Your link was not copied Please try again'),
            iconName: 'warning',
            background: theme.colors.statusErrorBackground,
            focusAfterToastDismissRef
          })
        }
      }
    } else {
      setState(SHARING_BTN_STATE.ERROR)
      app.bus.send('toast/show', {
        message: app.gt()('mapLinkShareButton:Your link was not copied Please try again'),
        iconName: 'warning',
        background: theme.colors.statusErrorBackground,
        focusAfterToastDismissRef
      })
    }
  }

  const onCopyButtonClick = async () => {
    const didCopySucceed = await copyToClipboard(shortUrl)
    if (didCopySucceed) {
      setState(SHARING_BTN_STATE.SUCCESS)
    } else {
      setState(SHARING_BTN_STATE.ERROR)
    }
  }

  if (state === SHARING_BTN_STATE.IN_PROGRESS)
    return <MapNavButton iconName="external.link" loader />
  if (state === SHARING_BTN_STATE.READY_TO_COPY)
    return <Tooltip
      onFirstRenderShowTooltip={{ sticky: true }}
      content={T('mapLinkShareButton:Your link is ready Click to copy to clipboard')}
      backgroundColor={theme.colors.statusSuccessBackground}
      arrowColor={theme.colors.statusSuccessBackground}
      borderColor={theme.colors.statusSuccessBackground}
      color={theme.colors.statusSuccessText}
    >
      <MapNavButton iconName="external.link" onClick={onCopyButtonClick} />
    </Tooltip>

  return <>
    <ToolTipComponent tooltipText={T('mapLinkShareButton:Copy link to clipboard')} arrowPosition='left' >
      <MapNavButton ref={focusAfterToastDismissRef} aria-label={T('mapLinkShareButton:Copy link to clipboard')} iconName="external.link" onClick={onButtonClick} data-cy={'LinkSharingButton'}/>
    </ToolTipComponent>
  </>
})

const createDesktopController = async (app) => {
  const installIdResponse = await app.bus.send('analytics2/getInstallId')
  const installId = installIdResponse && installIdResponse.length ? installIdResponse[0] : DEFAULT_INSTALL_ID
  app.bus.send('layers/register', {
    id: MAP_LINK_SHARE_BUTTON_DESKTOP,
    widget: () => <LinkSharingButton T={app.gt()} installId={installId} app={app} />,
    show: true,
    isOverlay: true,
    layoutId: 'buttonOptions',
    widgetType: widgetEnum.Desktop,
    order: 40
  })
}

const createMobileController = async (app) => {
  const T = app.gt()
  const theme = app.themePack.getTheme()
  const installIdResponse = await app.bus.send('analytics2/getInstallId')
  const installId = installIdResponse && installIdResponse.length ? installIdResponse[0] : DEFAULT_INSTALL_ID
  const showSuccessMessage = () => {
    app.bus.send('toast/show', {
      message: T('mapLinkShareButton:Link successfully copied to clipboard'),
      iconName: 'success',
      background: theme.colors.statusSuccessBackground,
      color: theme.colors.statusSuccessText
    })
  }
  const showFailMessage = () => {
    app.bus.send('toast/show', {
      message: T('mapLinkShareButton:Your link was not copied Please try again'),
      iconName: 'delete.text',
      background: theme.colors.statusErrorBackground,
      color: theme.colors.statusErrorText
    })
  }
  const onButtonTap = async () => {
    const shortenedUrl = await getShortenedMapUrl(installId)
    if (shortenedUrl) {
      if (isSafari) {
        copyToClipboard(shortenedUrl)
        if (navigator.share && typeof navigator.share === 'function') {
          navigator.share({ url: shortenedUrl })
          showSuccessMessage()
        } else
          showFailMessage()
      } else {
        const didCopySucceed = await copyToClipboard(shortenedUrl)
        if (didCopySucceed)
          showSuccessMessage()
        else
          showFailMessage()
      }
    } else {
      showFailMessage()
    }
  }

  app.bus.send('layers/register', {
    id: MAP_LINK_SHARE_BUTTON_MOBILE,
    widget: () => (
      <FloatingActionButton data-cy="mapLinkShareButton" iconName="external.link" onClick={onButtonTap} aria-label={T('mapLinkShareButton:Press to copy link to clipboard')} />
    ),
    shouldShow: currentlyShowing => {
      if (currentlyShowing.includes('SearchResultsBody') && currentlyShowing.includes('SearchResultsHeader')) return true
      const widgetsToCheck = ['SearchResultsHeader', 'DirectionsSearchControls', 'DirectionsResultControls', 'DirectionsResultMobile', 'poiViewMobile', 'SearchResultsCategoryList']
      return !widgetsToCheck.some(widget => currentlyShowing.includes(widget))
    },
    isOverlay: true,
    layoutId: 'mapControls',
    widgetType: widgetEnum.Mobile,
    order: 20
  })
}

function create (app, config) {
  const init = () => {}

  if (!config.hide) {
    createDesktopController(app)
    createMobileController(app)
  }

  return {
    init
  }
}

export { create }
